import {
  Text,
  Modal,
  InlineStack,
  BlockStack,
  Grid,
  Form,
  Divider,
  FormLayout,
  TextField,
  SkeletonBodyText,
  ResourceList,
  ResourceItem,
  Button,
  Select,
  Link,
} from '@shopify/polaris';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import helpers from 'helpers/index';
import { ENUM_ORDER_FEE, ORDER_FEE_LABEL, useGetOrder, useMutateOrderCancel, useMutateUpdateOrder } from 'queries/orders.query';
import { ConfettiIcon, DuplicateIcon, PlusIcon, XSmallIcon } from '@shopify/polaris-icons';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';
import { useDeleteUserAddressBooks, useMutateGetUserAddressBooksForButtomBar } from 'queries/user_address_book.query';
import { TypedCustomer } from 'interface/customer.model';
import __ from 'languages/index';
import AutocompleteAddress from 'components/addressAutoComplete';

import { useMutateSearchUser } from 'queries/users.query';
import { useGetMapsPlaceDetail } from 'queries/google_maps.suggest.query';
import { ENUM_TICKET_TYPE } from 'queries/ticket.query';
import { TypedAddress_suggestion, useMutateGetAddressSuggestions } from 'queries/address_suggestion.query';
import { QRCodeCanvas } from 'qrcode.react';
import dayjs from '../../utils/dayjs';

function takeNumberOnly(input: string): string {
  return input.replace(/\D/g, '');
}

export default function EditOrderModal({ show, order_id, onClose }: { show: boolean; order_id: string; onClose: (reload: boolean) => void }) {
  const { addNotification } = useNotification();
  const { mutateAsync: orderCancel, isPending: cancelling, isSuccess } = useMutateOrderCancel();
  const { mutateAsync: updateOrder, isPending: updating, isSuccess: updateSuccess } = useMutateUpdateOrder();
  const { data: orderData, refetch: reloadOrderData, isPending: loadingGetTicketViaOrderCode } = useGetOrder(order_id);

  const [isLocked, setIsLocked] = useState(false);
  useEffect(() => {
    setIsLocked(orderData?.is_locked === 1);
  }, [orderData]);

  const [currentCustomerData, setCurrentCustomerData] = useState<TypedCustomer>(null);

  const {
    mutateAsync: mutateGetUserAddressBook,
    data: addressBookData,
    isPending: loadingUserAddressBook,
    isError: loadingUserAddressError,
    reset: resetUserAddressBook,
  } = useMutateGetUserAddressBooksForButtomBar();

  const [userAddressBookData, setUserAddressBookData] = useState<{ from: any[]; to: any[] }>({ from: [], to: [] });
  useEffect(() => {
    if (addressBookData) {
      setUserAddressBookData(addressBookData);
    }
  }, [addressBookData]);

  const { mutateAsync: deleteUserAddressBook } = useDeleteUserAddressBooks();

  const deleteUserAddressCallback = useCallback(
    async (user_address_book_id: string) => {
      try {
        const c = window.confirm('Bạn có chắc chắn muốn xóa địa chỉ này không?');
        if (!c) return;

        await deleteUserAddressBook(user_address_book_id);
        addNotification('info', 'Xóa địa chỉ thành công!');
        setUserAddressBookData((prev) => ({
          from: prev.from.filter((el) => el.user_address_book_id !== user_address_book_id),
          to: prev.to.filter((el) => el.user_address_book_id !== user_address_book_id),
        }));
      } catch (e) {}
    },
    [deleteUserAddressBook, addNotification, setUserAddressBookData]
  );

  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** Ghi chú điểm đón trả */
  const [showPickupNote, setShowPickupNote] = useState(false);
  const [showDropoffNote, setShowDropoffNote] = useState(false);
  /** Show form thêm thông tin đón */
  const [showPickUpContactInformation, setShowPickUpContactInformation] = useState(false);

  useEffect(() => {
    if (orderData) {
      setCurrentCustomerData(orderData?.customer_data);
      setShowPickupNote(!!orderData?.pickup_note);
      setShowDropoffNote(!!orderData?.dropoff_note);
      setShowPickUpContactInformation(!!orderData?.pickup_contact_phonenumber);
    }
  }, [orderData]);

  const useFields = {
    /** create new one */
    trip_time: useField<string>({ value: orderData?.pickup_time || '', validates: [] }),
    trip_date: useField<string>({
      value: orderData?.departure_date ? dayjs(Number(orderData.departure_date)).utcOffset(7).format('HH:mm DD-MM-YYYY') : '',
      validates: [],
    }),

    /** Edit ... */
    id: useField<string>({ value: `` + orderData?.id, validates: [] }),
    customer_name: useField<string>({ value: currentCustomerData?.display_name ?? '', validates: [] }),
    customer_phone: useField<string>({ value: currentCustomerData?.user_phonenumber ?? '', validates: [] }),
    pickup_time: useField<string>({
      value: orderData?.pickup_time,
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isValidTime(inputVal)) return 'Giờ định dạng sai (hh:mm)';
        },
      ],
    }),

    pickup_address: useField<string>({ value: orderData?.pickup_address || '', validates: [notEmpty('Trường này không được để trống.')] }),
    pickup_place_id: useField<string>({
      value: orderData?.pickup_place_id || '',
      validates: [notEmpty('Hãy chọn từ một trong các địa chỉ hợp lệ.')],
    }),

    pickup_province: useField<string>({ value: orderData?.pickup_province, validates: [] }),
    pickup_district: useField<string>({ value: orderData?.pickup_district, validates: [] }),
    pickup_ward: useField<string>({ value: orderData?.pickup_ward, validates: [] }),

    pickup_note: useField<string>({ value: orderData?.pickup_note || '', validates: [] }),

    /** Case: Đặt hộ */
    pickup_contact_name: useField<string>({ value: orderData?.pickup_contact_name, validates: [] }),
    pickup_contact_phonenumber: useField<string>({
      value: orderData?.pickup_contact_phonenumber,
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isPhoneNumber(inputVal)) return 'Số điện thoại không hợp lệ';
        },
      ],
    }),

    dropoff_address: useField<string>({
      value: orderData?.dropoff_address || '',
      validates: [notEmpty('Trường này không được để trống.')],
    }),
    dropoff_contact_name: useField<string>({
      value: orderData?.dropoff_contact_name || '',
      validates: [],
    }),
    dropoff_contact_phonenumber: useField<string>({
      value: orderData?.dropoff_contact_phonenumber || '',
      validates: [],
    }),

    dropoff_place_id: useField<string>({
      value: orderData?.dropoff_place_id || '',
      validates: [notEmpty('Hãy chọn từ một trong các địa chỉ hợp lệ.')],
    }),

    dropoff_province: useField<string>({ value: orderData?.dropoff_province, validates: [] }),
    dropoff_district: useField<string>({ value: orderData?.dropoff_district, validates: [] }),
    dropoff_ward: useField<string>({ value: orderData?.dropoff_ward, validates: [] }),

    dropoff_note: useField<string>({ value: orderData?.dropoff_note || '', validates: [] }),

    note: useField<string>({ value: orderData?.note || '', validates: [lengthLessThan(1000, 'Ghi chú quá dài...')] }),

    // price: useField<number>({
    //   value: ticketData?.price || 0,
    //   validates: [
    //     (inputVal) => {
    //       if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
    //     },
    //   ],
    // }),
    // discount: useField<number>({
    //   value: ticketData?.discount || 0,
    //   validates: [
    //     (inputVal) => {
    //       if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
    //       if (Number(inputVal) < 0) return 'Phải là một số dương hợp lệ.';
    //     },
    //   ],
    // }),

    price_table: useField<{ price: number; discount: number; ticket_id: bigint; ticket_type: string }[]>({
      value: [{ price: 0, discount: 0, ticket_id: BigInt('0'), ticket_type: ENUM_TICKET_TYPE.PASSENGER }],
      validates: [],
    }),

    order_fee: useField<{ fee_value: number; fee_description: string }[]>({
      value: orderData?.order_fee ? orderData.order_fee.map((fD) => ({ fee_value: fD.fee_value, fee_description: fD.fee_description })) : [],
      validates: [],
    }),

    total_fee: useField<number>({
      value: orderData?.total_fee || 0,
      validates: [],
    }),

    payment_status: useField<number>({ value: orderData?.payment_status || 0, validates: [] }), // read only
  };

  /** phải load dữ liệu khi show modal */
  useEffect(() => {
    if (show) {
      reloadOrderData();
    } else {
      helpers.resetForm(useFields);
    }
  }, [show]);

  const { fields, submit, submitting, dirty, makeClean, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateOrder({
          id: values.id,
          customer_name: values.customer_name,
          customer_phone: values.customer_phone,
          pickup_time: values.pickup_time || undefined,

          pickup_address: values.pickup_address,
          pickup_place_id: values.pickup_place_id,
          pickup_district: values.pickup_district,
          pickup_province: values.pickup_province,
          pickup_ward: values.pickup_ward,

          pickup_note: values.pickup_note,
          pickup_contact_name: values.pickup_contact_name,
          pickup_contact_phonenumber: values.pickup_contact_phonenumber,

          dropoff_address: values.dropoff_address,
          dropoff_contact_name: values.dropoff_contact_name,
          dropoff_contact_phonenumber: values.dropoff_contact_phonenumber,
          dropoff_place_id: values.dropoff_place_id,
          dropoff_note: values.dropoff_note,

          dropoff_district: values.dropoff_district,
          dropoff_province: values.dropoff_province,
          dropoff_ward: values.dropoff_ward,

          note: values.note,
          // price: values.price,
          // fee: values.fee,
          // discount: values.discount,
          price_table: values.price_table,
          order_fee: values.order_fee /** special */,

          /** đừng truyền vào đây */
          // departure_date: new Date(values.trip_date + ' ' + values.trip_time).getTime(), // String(values.trip_time || '').trim() + ' ' + String(values.trip_date || '').trim(),
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  const fromClipboard = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      fields.customer_phone.onChange(takeNumberOnly(clipboardText));
    } catch (e) {}
  }, []);

  const { mutateAsync: searchUser } = useMutateSearchUser();
  const loadCustomerDataSuggestion = useCallback(async (phonenumber: string) => {
    try {
      let customerData = await searchUser({
        user_phonenumber: phonenumber,
      });
      if (!helpers.isEmpty(customerData)) {
        // @ts-ignore
        setCurrentCustomerData(customerData[0]);
      }
    } catch (e) {}
  }, []);

  const lastSearchedPhone = useRef<string>('');
  const searchCustomerDebounce = useMemo(
    () =>
      helpers.debounce((value: string) => {
        if (!value || value === lastSearchedPhone.current) return;
        lastSearchedPhone.current = value;
        loadCustomerDataSuggestion(value);
      }, 400),
    [loadCustomerDataSuggestion]
  );

  useEffect(() => {
    const normalizedPhone = takeNumberOnly(fields.customer_phone.value);
    if (helpers.isPhoneNumber(normalizedPhone)) {
      searchCustomerDebounce(normalizedPhone);
    } else {
      lastSearchedPhone.current = '';
    }
  }, [fields.customer_phone.value, searchCustomerDebounce]);

  /** reset lai form */
  useEffect(() => {
    helpers.resetForm(useFields);
    makeClean();
    resetUserAddressBook();
  }, [show]);

  useEffect(() => {
    if (!currentCustomerData) return;
    if (!orderData) return;
    /** đơn bị khóa rồi thì không load nữa ... */
    if (orderData.is_locked === 1) return;

    mutateGetUserAddressBook({
      journey_id: String(orderData?.journey_id),
      user_id: currentCustomerData?.user_id,
    });
  }, [currentCustomerData, orderData]);

  /** OLD */
  useEffect(() => {
    if (isSuccess) {
      addNotification('info', 'Hủy vé thành công!');
      onClose(true);
    }
  }, [isSuccess]);
  useEffect(() => {
    if (updateSuccess) {
      addNotification('info', 'Cập nhật vé thành công!');
      onClose(true);
    }
  }, [updateSuccess]);

  const orderCancelCallback = useCallback(async () => {
    let result = window.confirm('Bạn có thực sự muốn hủy đơn này và các vé liên quan?');
    try {
      if (result) await orderCancel(order_id);
    } catch (e) {}
  }, [order_id]);

  // const { mutateAsync: getQRImage } = useMutateGetQRImage();
  // const [qrCodeImageRef, setQrCodeImageRef] = useState(null);
  // const generateQRCodeForPaymentCallback = useCallback(
  //   async (order_id: string) => {
  //     try {
  //       const QRCodeData = await getQRImage({ order_id, manifest_id });
  //       // Hiển thị QR code trong một popover
  //       setQrCodeImageRef({
  //         qr_code_data: QRCodeData.qr_code_data,
  //         timestamp: QRCodeData.timestamp,
  //       });
  //     } catch (e) {
  //       addNotification('error', 'Không thể tạo QR code cho đơn hàng này.');
  //     }
  //   },
  //   [order_id, manifest_id, addNotification]
  // );

  /** tính toán tổng phải thanh toán */
  const [totalMustPay, setTotalMustPay] = useState<number>(0);

  useEffect(() => {
    const fee = Number(fields.total_fee.value || 0);
    let totalMustPay = 0;

    let allPriceTable = useFields.price_table.value;
    for (let priceTag of allPriceTable) {
      totalMustPay += priceTag.price - priceTag.discount;
    }
    setTotalMustPay(totalMustPay + fee);
  }, [fields.price_table.value, fields.total_fee.value]);

  useEffect(() => {
    if (helpers.isEmpty(useFields.order_fee.value)) return;
    let total_fee = 0;
    for (let d of useFields.order_fee.value) {
      total_fee += d.fee_value;
    }
    useFields.total_fee.onChange(total_fee);
  }, [useFields.order_fee.value]);

  /** set gía khi edit */
  const [has_shipping_package, setHas_shipping_package] = useState(false);
  useEffect(() => {
    if (!orderData) return;
    if (!show) return;
    let price_table = [];
    for (let ticket of orderData.ticket) {
      // vé đã xóa thì bỏ đi
      if (ticket.markAsDeleted === 1) continue;
      // chỉ cần một vé có giao hàng ...
      if (ticket.ticket_type === ENUM_TICKET_TYPE.SHIPMENT) setHas_shipping_package(true);
      price_table.push({ price: ticket?.price, discount: ticket?.discount, ticket_id: ticket.ticket_id });
    }
    useFields.price_table.onChange(price_table);
  }, [orderData, show]);

  /** Tính khoảng cách và chi phí thêm */
  const { mutateAsync: getLatlongfromPlaceID } = useGetMapsPlaceDetail();

  const upsertOrderFee = useCallback(
    (feeDescription: string, feeValue: number) => {
      const normalizedFeeValue = helpers.parseNumeric(feeValue);
      const currentFees = [...fields.order_fee.value];
      const targetIndex = currentFees.findIndex((fee) => fee.fee_description === feeDescription);

      if (normalizedFeeValue > 0) {
        if (targetIndex >= 0) {
          currentFees[targetIndex] = { ...currentFees[targetIndex], fee_value: normalizedFeeValue };
          fields.order_fee.onChange(currentFees);
          return;
        }
        fields.order_fee.onChange([
          ...currentFees,
          {
            fee_value: normalizedFeeValue,
            fee_description: feeDescription,
          },
        ]);
        return;
      }

      if (targetIndex >= 0) {
        fields.order_fee.onChange(currentFees.filter((_, idx) => idx !== targetIndex));
      }
    },
    [fields.order_fee.value]
  );

  // const updatePickupTimeWithMinute = useCallback(
  //   (minute?: number | string) => {
  //     if (minute && helpers.isNumeric(minute)) {
  //       const [hour, minuteStr] = String(fields.trip_time.value || fields.pickup_time.value || '').split(':');
  //       const baseDate = new Date();
  //       baseDate.setHours(Number(hour || 0));
  //       baseDate.setMinutes(Number(minuteStr || 0));
  //       baseDate.setSeconds(0);
  //       baseDate.setMilliseconds(0);
  //       baseDate.setMinutes(baseDate.getMinutes() + Number(minute));

  //       const hh = String(baseDate.getHours()).padStart(2, '0');
  //       const mm = String(baseDate.getMinutes()).padStart(2, '0');
  //       fields.pickup_time.onChange(`${hh}:${mm}`);
  //     }
  //   },
  //   [fields.trip_time.value, fields.pickup_time.value]
  // );

  const applyExtraInfoFromAddress = useCallback(
    ({
      extra_price,
      note,
      pickup_or_dropoff = 'pickup',
    }: {
      extra_price?: number | string;
      note?: string;
      minute?: number | string;
      pickup_or_dropoff?: 'pickup' | 'dropoff';
    }) => {
      if (pickup_or_dropoff === 'pickup') {
        if (extra_price !== undefined && extra_price !== null) {
          upsertOrderFee('PICKUP_FEE', Number(extra_price));
        }
        if (note) {
          setShowPickupNote(true);
        }
        fields.pickup_note.onChange(note);
        return;
      }

      if (extra_price !== undefined && extra_price !== null) {
        upsertOrderFee('DROPOFF_FEE', Number(extra_price));
      }
      if (note) {
        setShowDropoffNote(true);
      }
      fields.dropoff_note.onChange(note);
    },
    [fields.pickup_note, fields.dropoff_note, upsertOrderFee]
  );

  const fetchExtraInfoFromSavingAddress = useCallback(
    async (item: any, pickup_or_dropoff: 'pickup' | 'dropoff' = 'pickup') => {
      try {
        const { user_address_meta } = item || {};
        const { extra_price, minute, note, place_id } = user_address_meta || {};

        applyExtraInfoFromAddress({ extra_price, minute, note, pickup_or_dropoff });

        if (place_id) {
          const { commune, province } = await getLatlongfromPlaceID(place_id);

          if (pickup_or_dropoff === 'pickup') {
            fields.pickup_province.onChange(province);
            fields.pickup_ward.onChange(commune);
          } else {
            fields.dropoff_province.onChange(province);
            fields.dropoff_ward.onChange(commune);
          }
        }
      } catch (e) {}
    },
    [fields, applyExtraInfoFromAddress, getLatlongfromPlaceID]
  );

  /** Thêm phí */

  const handleAddOrderFee = useCallback(() => {
    useFields.order_fee.onChange([...useFields.order_fee.value, { fee_description: 'PICKUP_FEE', fee_value: 0 }]);
  }, [useFields.order_fee.value]);

  const handleRemoveOrderFee = useCallback(
    (index: number) => {
      const next = useFields.order_fee.value.filter((_, i) => i !== index);
      useFields.order_fee.onChange(next);
    },
    [useFields.order_fee.value, useFields.order_fee]
  );

  // Nuts copy
  const copyReceiverFromSenderCallback = useCallback(() => {
    useFields.dropoff_contact_name.onChange(useFields.customer_name.value);
    useFields.dropoff_contact_phonenumber.onChange(useFields.customer_phone.value);
  }, [useFields.customer_name.value, useFields.customer_phone.value]);

  /** Note: Tạm thời tắt chức năng hủy đơn và toàn bộ vé */

  /** Lấy địa chỉ gợi ý */
  const { mutateAsync: getAddressSuggestion } = useMutateGetAddressSuggestions();
  const [suggestDataFrom, setSuggestDataFrom] = useState<TypedAddress_suggestion[]>([]);
  const [suggestDataTo, setSuggestDataTo] = useState<TypedAddress_suggestion[]>([]);

  const fetchSuggestionData = useCallback(async () => {
    let FromData = await getAddressSuggestion({
      journey_from_id: orderData?.journey.journey_from_province_id,
      journey_id: orderData?.journey.id,
      limit: 5,
    });
    if (!helpers.isEmpty(FromData)) setSuggestDataFrom(FromData);

    let ToData = await getAddressSuggestion({
      journey_from_id: orderData?.journey.journey_to_province_id,
      journey_id: orderData?.journey?.id,
      limit: 5,
    });
    if (!helpers.isEmpty(ToData)) setSuggestDataTo(ToData);
  }, [orderData]);

  useEffect(() => {
    if (!orderData || helpers.isEmpty(orderData)) return;
    if (orderData.is_locked === 0) {
      fetchSuggestionData();
    }
  }, [orderData]);

  return (
    <>
      <Modal
        size="large"
        open={show}
        title={`Chi tiết đơn hàng ${orderData?.order_code} ID #${orderData?.id} - ${currentCustomerData ? `${currentCustomerData?.display_name} - ${currentCustomerData?.user_phonenumber}` : 'Chưa có thông tin khách hàng'}`}
        onClose={() => onClose(false)}
        secondaryActions={[
          {
            content: 'Hủy đơn & toàn bộ vé',
            icon: XSmallIcon,
            disabled: cancelling || isLocked,
            onAction: () => orderCancelCallback(),
          },
        ]}
        primaryAction={{
          content: `Cập nhật`,
          icon: ConfettiIcon,
          disabled: cancelling || !dirty || isLocked,
          loading: submitting,
          onAction: submit,
        }}
      >
        <Modal.Section>
          <Form onSubmit={submit}>
            <Grid columns={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
              <Grid.Cell>
                <BlockStack gap={{ xs: '200', sm: '400' }}>
                  <FormLayout.Group>
                    <TextField
                      label="Số điện thoại"
                      autoComplete="off"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      disabled={submitting || isLocked}
                      clearButton
                      value={fields.customer_phone.value}
                      labelAction={{
                        content: 'Dán',
                        onAction: () => {
                          fromClipboard();
                        },
                      }}
                      onChange={(v) => {
                        fields.customer_phone.onChange(takeNumberOnly(String(v)));
                      }}
                      error={fields.customer_phone.error}
                      onClearButtonClick={() => {
                        fields.customer_phone.onChange('');
                        setCurrentCustomerData(null);
                      }}
                    />

                    <TextField
                      autoComplete="off"
                      placeholder="Tên khách hàng"
                      label="Khách hàng"
                      {...fields.customer_name}
                      onChange={(e) => fields.customer_name.onChange(helpers.capitalizeWords(e))}
                      disabled={submitting || isLocked}
                    />
                  </FormLayout.Group>
                  <BlockStack gap="200">
                    <div>
                      <Button variant="plain" icon={PlusIcon} onClick={() => setShowPickUpContactInformation((old) => !old)}>
                        Đặt hộ người khác?
                      </Button>
                    </div>

                    {showPickUpContactInformation ? (
                      <FormLayout.Group>
                        <TextField
                          label=""
                          placeholder="Số điện thoại"
                          autoComplete="off"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          disabled={submitting || isLocked}
                          value={fields.pickup_contact_phonenumber.value}
                          onChange={(v) => {
                            fields.pickup_contact_phonenumber.onChange(String(v).trim());
                            // searchCustomerDebound();
                          }}
                          // onBlur={() => loadCustomerDataSuggestion()}
                          error={fields.pickup_contact_phonenumber.error}
                          clearButton
                          onClearButtonClick={() => {
                            fields.pickup_contact_phonenumber.onChange('');
                            setCurrentCustomerData(null);
                          }}
                        />

                        <TextField
                          autoComplete="off"
                          placeholder="Tên khách hàng"
                          label=""
                          {...fields.pickup_contact_name}
                          onChange={(e) => fields.pickup_contact_name.onChange(helpers.capitalizeWords(e))}
                          disabled={submitting || isLocked}
                          clearButton
                          onClearButtonClick={() => fields.pickup_contact_name.onChange('')}
                        />
                      </FormLayout.Group>
                    ) : null}
                  </BlockStack>

                  <Divider />
                  <AutocompleteAddress
                    address_suggestion={suggestDataFrom}
                    autoComplete="off"
                    latlong={orderData?.journey?.journey_from_lat_long}
                    journey_id={orderData?.journey?.id}
                    placeholder="Địa chỉ đón"
                    prependAddress={orderData?.journey?.journey_from}
                    label={`Đón tại ` + orderData?.journey?.journey_from}
                    selectedCallback={(out) => {
                      const { latlng, ward, province, extra_price, note, pickup_time, pickup_support, position_name } = out;
                      useFields.pickup_place_id.onChange(latlng);
                      useFields.pickup_province.onChange(province);
                      useFields.pickup_ward.onChange(ward);
                      applyExtraInfoFromAddress({
                        extra_price,
                        note: note || position_name ? `[Vùng: ${position_name}${pickup_support === 0 ? ' **Không hỗ trợ**' : ''}] ${note}` : '',
                        pickup_or_dropoff: 'pickup',
                      });
                    }}
                    {...useFields.pickup_address}
                    disabled={submitting || isLocked}
                    labelAction={{
                      content: 'thêm thông tin',
                      onAction: () => {
                        if (showPickupNote) {
                          setShowPickupNote(false);
                          fields.pickup_note.onChange('');
                        } else {
                          setShowPickupNote(true);
                        }
                      },
                    }}
                    error={useFields.pickup_place_id.error}
                  />

                  {showPickupNote && (
                    <>
                      <TextField
                        autoComplete="off"
                        multiline={2}
                        tone="magic"
                        label="Ghi chú điểm đón"
                        placeholder="Ghi chú"
                        disabled={isLocked}
                        {...fields.pickup_note}
                      />
                    </>
                  )}

                  {loadingUserAddressBook ? (
                    <SkeletonBodyText />
                  ) : helpers.isEmpty(userAddressBookData?.from) ? (
                    <></>
                  ) : (
                    <>
                      <ResourceList
                        resourceName={{ singular: 'Địa chỉ đón', plural: 'Địa chỉ đón' }}
                        emptyState={<>Chưa có thông tin lưu sẵn!</>}
                        items={userAddressBookData?.from || []}
                        renderItem={(item, index) => {
                          return (
                            <ResourceItem
                              id={item.user_address_book_id}
                              // url={''}
                              onClick={() => {
                                useFields.pickup_address.onChange(item.user_address);
                                useFields.pickup_province.onChange(item.user_address_province);
                                useFields.pickup_ward.onChange(item.user_address_ward);
                                useFields.pickup_place_id.onChange(item.user_address_place_id);
                                useFields.pickup_note.onChange(item.note);
                                if (item.note) {
                                  setShowPickupNote(true);
                                }
                                fetchExtraInfoFromSavingAddress(item, 'pickup');
                              }}
                              shortcutActions={[
                                {
                                  content: 'Xoá',
                                  onAction: () => deleteUserAddressCallback(item.user_address_book_id),
                                },
                              ]}
                              persistActions
                            >
                              <Text fontWeight="bold" as="p" tone="subdued">
                                {item.user_address}
                              </Text>
                              {item.note && (
                                <Text as="p" tone="critical">
                                  ⚠ {item.note}
                                </Text>
                              )}
                            </ResourceItem>
                          );
                        }}
                      />
                    </>
                  )}

                  <AutocompleteAddress
                    autoComplete="off"
                    address_suggestion={suggestDataTo}
                    latlong={orderData?.journey?.journey_to_lat_long}
                    journey_id={orderData?.journey?.id}
                    placeholder="Địa chỉ xuống"
                    prependAddress={orderData?.journey?.journey_to}
                    label={`Xuống tại ` + orderData?.journey?.journey_to}
                    selectedCallback={(out) => {
                      const { latlng, ward, province, extra_price, note, position_name, pickup_support } = out;
                      useFields.dropoff_place_id.onChange(latlng);
                      useFields.dropoff_province.onChange(province);
                      useFields.dropoff_ward.onChange(ward);
                      applyExtraInfoFromAddress({
                        extra_price,
                        note: note || position_name ? `[Vùng: ${position_name}${pickup_support === 0 ? ' **Không hỗ trợ**' : ''}] ${note}` : '',
                        pickup_or_dropoff: 'dropoff',
                      });
                    }}
                    {...useFields.dropoff_address}
                    disabled={submitting || isLocked}
                    labelAction={{
                      content: 'ghi chú',
                      onAction: () => {
                        if (showDropoffNote) {
                          setShowDropoffNote(false);
                          fields.dropoff_note.onChange('');
                        } else {
                          setShowDropoffNote(true);
                        }
                      },
                    }}
                    error={useFields.dropoff_place_id.error}
                  />

                  {showDropoffNote && (
                    <TextField
                      autoComplete="off"
                      multiline={2}
                      disabled={isLocked}
                      label="Ghi chú điểm trả"
                      placeholder="Ghi chú"
                      {...fields.dropoff_note}
                    />
                  )}

                  {loadingUserAddressBook ? (
                    <SkeletonBodyText />
                  ) : helpers.isEmpty(userAddressBookData?.to) ? (
                    <></>
                  ) : (
                    <ResourceList
                      resourceName={{ singular: 'Địa chỉ trả', plural: 'Địa chỉ trả' }}
                      emptyState={<p>Chưa có thông tin lưu sẵn.</p>}
                      items={userAddressBookData?.to || []}
                      renderItem={(item, index) => {
                        return (
                          <ResourceItem
                            id={item.user_address_book_id}
                            // url={''}
                            onClick={() => {
                              useFields.dropoff_address.onChange(item.user_address);
                              useFields.dropoff_place_id.onChange(item.user_address_place_id);
                              useFields.dropoff_province.onChange(item.user_address_province);
                              useFields.dropoff_ward.onChange(item.user_address_ward);
                              useFields.dropoff_note.onChange(item.note);
                              if (item.note) {
                                setShowDropoffNote(true);
                              }
                              fetchExtraInfoFromSavingAddress(item, 'dropoff');
                            }}
                            shortcutActions={[
                              {
                                content: 'Xoá',
                                onAction: () => deleteUserAddressCallback(item.user_address_book_id),
                              },
                            ]}
                            persistActions
                          >
                            <Text fontWeight="bold" as="p" tone="subdued">
                              {item.user_address}
                            </Text>
                            {item.note && (
                              <Text as="p" tone="critical">
                                ⚠ {item.note}
                              </Text>
                            )}
                          </ResourceItem>
                        );
                      }}
                    />
                  )}
                </BlockStack>
                {has_shipping_package ? (
                  <BlockStack gap="400" inlineAlign="start">
                    <Divider />
                    <Text as="h3" variant="headingMd">
                      Thông tin người nhận hàng
                    </Text>
                    <FormLayout.Group condensed>
                      <TextField
                        autoComplete="off"
                        label={`Tên người nhận hàng`}
                        {...fields.dropoff_contact_name}
                        requiredIndicator
                        disabled={submitting || isLocked}
                      />
                      <TextField
                        autoComplete="off"
                        label={`Số liên lạc`}
                        {...fields.dropoff_contact_phonenumber}
                        requiredIndicator
                        disabled={submitting || isLocked}
                      />
                    </FormLayout.Group>
                    <Button
                      icon={DuplicateIcon}
                      variant="plain"
                      onClick={() => {
                        copyReceiverFromSenderCallback();
                      }}
                      disabled={isLocked}
                    >
                      Copy thông tin từ người đặt
                    </Button>
                  </BlockStack>
                ) : null}
                {/** MÃ QR ĐỂ GỌI NHANH */}
                <br />
                <Link url={`tel:${orderData?.customer_data?.user_phonenumber}`}>
                  <QRCodeCanvas title={orderData?.customer_data?.user_phonenumber} size={60} value={orderData?.customer_data?.user_phonenumber} />
                </Link>
                <Divider />
              </Grid.Cell>
              <Grid.Cell>
                <BlockStack gap={{ xs: '200', sm: '400' }}>
                  <Text as="h3" variant="headingMd">
                    {orderData?.journey?.journey_name}
                  </Text>
                  <FormLayout.Group condensed>
                    {/* <TextField autoComplete="off" label={`Thời gian`} placeholder="hh:mm" {...fields.trip_time} disabled={submitting} /> */}
                    <TextField autoComplete="off" label={`Đón lúc`} placeholder="hh:mm" {...fields.pickup_time} disabled={submitting || isLocked} />
                    <TextField autoComplete="off" label={`Ngày đi`} value={`${fields.trip_date.value}`} disabled={true} />
                  </FormLayout.Group>
                  <Divider />

                  <TextField
                    autoComplete="off"
                    multiline={2}
                    label="Ghi chú đơn hàng"
                    showCharacterCount
                    maxLength={1000}
                    {...fields.note}
                    disabled={submitting || isLocked}
                  />

                  <Text as="h3" variant="headingMd">
                    Phí xử lý đơn
                  </Text>
                  {useFields.order_fee.value?.map((feeData, index) => {
                    return (
                      <FormLayout.Group key={`order_fee_${index}`}>
                        <Select
                          label="Tên"
                          options={helpers.enumToArray(ENUM_ORDER_FEE).map((el) => {
                            return {
                              label: ORDER_FEE_LABEL[el],
                              value: el,
                            };
                          })}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.order_fee.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], fee_description: a };
                            useFields.order_fee.onChange(updatedPriceTable);
                          }}
                          value={feeData.fee_description}
                          disabled={isLocked}
                        />
                        <TextField
                          autoComplete="off"
                          label={`Số tiền`}
                          type="tel"
                          placeholder="Số tiền"
                          inputMode="numeric"
                          pattern="[0-9.,]*"
                          value={__helpers.formatNumber(feeData.fee_value)}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.order_fee.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], fee_value: __helpers.parseNumeric(a) };
                            useFields.order_fee.onChange(updatedPriceTable);
                          }}
                          disabled={submitting || isLocked}
                          labelAction={{
                            content: 'Xoá',
                            onAction: () => handleRemoveOrderFee(index),
                          }}
                        />
                      </FormLayout.Group>
                    );
                  })}
                  <div>
                    <Button icon={PlusIcon} disabled={isLocked} variant="plain" onClick={handleAddOrderFee}>
                      Thêm phí
                    </Button>
                  </div>

                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="span" fontWeight="bold">
                      Tổng phí
                    </Text>
                    <Text as="h3" fontWeight="bold" variant="headingMd" tone="critical">
                      {__helpers.formatNumber(fields.total_fee.value)} VND
                    </Text>
                  </InlineStack>

                  <Divider />

                  <Text as="h3" variant="headingMd">
                    Vé và giá vé
                  </Text>

                  {useFields.price_table.value.map((el, index) => {
                    return (
                      <FormLayout.Group condensed key={`price_${index}_${el.ticket_id}`}>
                        <TextField
                          autoComplete="off"
                          label={`Giá vé #${index + 1}`}
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9.,]*"
                          value={__helpers.formatNumber(el.price)}
                          // onChange={(a) => {
                          //   fields.price.onChange(helpers.parseNumeric(a));
                          // }}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.price_table.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], price: __helpers.parseNumeric(a) };
                            useFields.price_table.onChange(updatedPriceTable);
                          }}
                          disabled={submitting || isLocked}
                        />
                        <TextField
                          autoComplete="off"
                          label="Giảm giá"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={__helpers.formatNumber(el.discount)}
                          // onChange={(a) => {
                          //   fields.discount.onChange(helpers.parseNumeric(a));
                          // }}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.price_table.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], discount: __helpers.parseNumeric(a) };
                            useFields.price_table.onChange(updatedPriceTable);
                          }}
                          disabled={submitting || isLocked}
                        />
                      </FormLayout.Group>
                    );
                  })}

                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="span" fontWeight="bold">
                      Tổng phải thanh toán
                    </Text>
                    <Text as="h3" fontWeight="bold" variant="headingLg" tone="critical">
                      {__helpers.formatNumber(totalMustPay)} VND
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Grid.Cell>
            </Grid>
          </Form>
        </Modal.Section>
      </Modal>
    </>
  );
}

